import { type ApiError, useApi } from '@omnipresentgroup/api';
import { HTTPStatusCodes } from '@omnipresentgroup/constants';
import { camelToSnake } from '@omnipresentgroup/helpers';
import type {
  BaseExpense,
  ExpenseCategoryEnum,
  ExpenseStatusEnum,
  GetExpensesResponse,
  GetExpensesResponseAPI,
  SortOrder,
} from '@omnipresentgroup/types';
import { useQuery } from '@tanstack/react-query';

import { API_PATH, COMPENSATION_API_BASE } from '../constants';
import { transformGetExpensesResponseFromAPI } from '../mappers';

type ExpensesFilter = {
  employeeId?: string;
  status?: ExpenseStatusEnum;
  category?: ExpenseCategoryEnum;
  dateFrom?: string;
  dateTo?: string;
};

type SortingParameters = {
  sort: keyof BaseExpense;
  order: SortOrder;
};

export const useGetManagerExpenses = ({
  clientId,
  page,
  size,
  employeeId,
  status,
  category,
  dateFrom,
  dateTo,
  sorting,
}: {
  clientId?: string;
  page: number;
  size: number;
  sorting: SortingParameters;
} & ExpensesFilter) => {
  const { GET } = useApi();

  return useQuery<GetExpensesResponseAPI, ApiError, GetExpensesResponse>({
    queryKey: [
      API_PATH,
      clientId,
      page,
      size,
      employeeId,
      status,
      category,
      dateFrom,
      dateTo,
      sorting,
    ],
    queryFn: () =>
      GET<GetExpensesResponseAPI>(
        `${COMPENSATION_API_BASE}/${API_PATH}/approver/${clientId}`,
        {},
        {
          ...(page && { page: String(page) }),
          ...(size && { size: String(size) }),
          ...(employeeId && { employee_id: employeeId }),
          ...(status && { status: status }),
          ...(category && { category: category }),
          ...(dateFrom && { date_from: dateFrom }),
          ...(dateTo && { date_to: dateTo }),
          ...(sorting && {
            sort: camelToSnake(sorting.sort),
            order: sorting.order,
          }),
        },
      ),
    select: transformGetExpensesResponseFromAPI,
    throwOnError: (error) => error.status !== HTTPStatusCodes.NOT_FOUND,
    enabled: !!clientId,
  });
};
